import './styles.css'
import { useParams, useNavigate } from "react-router";
import { productsSelectors, fetchProductById, fetchRelatedProducts } from "../../features/products";
import { useDispatch, useSelector } from "react-redux";
import { ConfigProvider, Rate, Select, Button, Tabs, Skeleton } from 'antd';
import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { RightOutlined, LeftOutlined, MinusOutlined, PlusOutlined, DeleteFilled, ShoppingCartOutlined, HeartOutlined, HeartFilled, GiftFilled } from '@ant-design/icons';
import { addToCart, cartActions, changeCartItemQuantity, cartSelectors } from "../../features/cart";
import { toggleFavorite } from "../../features/favorites";
import { selectIsItemFavorite } from "../../features/favorites";
import { fetchReviewsList, fetchNextReviewsPage, reviewsSelectors } from "../../features/reviews";
import { calcBonusPoints } from "../../features/bonuses";
import ItemCard from "../../components/ItemCard";
import ItemCardSkeleton from "../../components/ItemCardSkeleton";
import {
    hasPackagingOptions,
    hasCityValues,
    getUniquePackagings,
    getCitiesForPackaging,
    findOption,
    getSelectLabels,
    isFirstSelectSearchable,
} from '../../utils/packagingHelpers';

// Fallback для товаров БЕЗ packaging_options
const getVariationAttributes = (attributes) => {
    if (!Array.isArray(attributes)) return [];
    return attributes.filter(a => a.variation === true && a.options?.length > 0 && a.slug !== 'pa_warehouse');
};

const getNonVariationAttributes = (attributes) => {
    if (!Array.isArray(attributes)) return [];
    return attributes.filter(a => !a.variation && a.visible && a.options?.length > 0);
};

const safeDecodeURI = (str) => { try { return decodeURIComponent(str); } catch { return str; } };
const toSelectOptions = (options) => options.map(opt => ({ value: opt, label: safeDecodeURI(opt) }));
const toPackagingSelectOptions = (options) => options.map(opt => ({ value: opt, label: formatPackagingLabel(opt) }));
const stripHtml = (html) => { if (!html) return ''; return html.replace(/<[^>]*>/g, '').trim(); };
const formatPrice = (value) => { const num = Math.round(Number(value)); if (isNaN(num)) return null; return `${num.toLocaleString('ru-RU')}₽`; };

const ProductSkeleton = () => (
    <div className='product-page-container'>
        <div className='product-top-section'>
            <div className='product-image-block'>
                <div className='skeleton-shimmer' style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: '4px' }} />
            </div>
            <div className='product-info-block'>
                <div className='skeleton-shimmer' style={{ width: '80%', height: 28, marginBottom: 16 }} />
                <div className='skeleton-shimmer' style={{ width: '40%', height: 16, marginBottom: 24 }} />
                <div className='skeleton-shimmer' style={{ width: '100%', height: 14, marginBottom: 8 }} />
                <div className='skeleton-shimmer' style={{ width: '100%', height: 14, marginBottom: 8 }} />
                <div className='skeleton-shimmer' style={{ width: '60%', height: 14, marginBottom: 24 }} />
                <div className='skeleton-shimmer' style={{ width: '30%', height: 12, marginBottom: 8 }} />
                <div className='skeleton-shimmer' style={{ width: '45%', height: 16 }} />
            </div>
            <div className='product-price-card' style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'none' }}>
                <div className='skeleton-shimmer' style={{ width: '55%', height: 32, marginBottom: 24 }} />
                <div className='skeleton-shimmer' style={{ width: '35%', height: 12, marginBottom: 8 }} />
                <div className='skeleton-shimmer' style={{ width: '100%', height: 36, marginBottom: 16 }} />
                <div className='skeleton-shimmer' style={{ width: '35%', height: 12, marginBottom: 8 }} />
                <div className='skeleton-shimmer' style={{ width: '100%', height: 36, marginBottom: 24 }} />
                <div className='skeleton-shimmer' style={{ width: '100%', height: 44, borderRadius: '6px' }} />
            </div>
        </div>
        <div className='product-divider' />
        <div className='product-full-description'>
            <div className='skeleton-shimmer' style={{ width: '100%', height: 14, marginBottom: 10 }} />
            <div className='skeleton-shimmer' style={{ width: '100%', height: 14, marginBottom: 10 }} />
            <div className='skeleton-shimmer' style={{ width: '90%', height: 14, marginBottom: 10 }} />
            <div className='skeleton-shimmer' style={{ width: '95%', height: 14, marginBottom: 10 }} />
            <div className='skeleton-shimmer' style={{ width: '70%', height: 14 }} />
        </div>
    </div>
);

const getMinPrice = (opts) => {
    if (!Array.isArray(opts) || opts.length === 0) return null;
    const prices = opts.map(o => Number(o.price)).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
};

const BonusPointsBadge = ({ price }) => {
    const points = calcBonusPoints(price);
    if (points <= 0) return null;
    return (
        <div className='price-card-bonuses'>
            <GiftFilled className='price-card-bonuses-icon' />
            +{points.toLocaleString('ru-RU')} бонусов за покупку
        </div>
    );
};

const PriceDisplay = ({ product, matchedOption }) => {
    if (matchedOption) return (<><div className='price-card-header'><span className='price-current'>{formatPrice(matchedOption.price)}</span></div><BonusPointsBadge price={matchedOption.price} /></>);
    const { price, regular_price, sale_price, on_sale, packaging_options } = product;
    const minPrice = getMinPrice(packaging_options);
    if (minPrice && !matchedOption) {
        return (<><div className='price-card-header'><span className='price-current'>от {formatPrice(minPrice)}</span></div><BonusPointsBadge price={minPrice} /></>);
    }
    if (on_sale && regular_price && (sale_price || price)) {
        return (<><div className='price-card-header'><span className='price-old'>{formatPrice(regular_price)}</span><span className='price-current'>{formatPrice(sale_price || price)}</span></div><BonusPointsBadge price={sale_price || price} /></>);
    }
    return (<><div className='price-card-header'><span className='price-current'>{formatPrice(price) || 'Цена не указана'}</span></div><BonusPointsBadge price={price} /></>);
};

const RelatedProducts = ({ productId }) => {
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);
    const relatedProducts = useSelector((state) => productsSelectors.selectRelatedProducts(state, productId));
    const isLoading = useSelector((state) => productsSelectors.selectIsRelatedLoading(state, productId));

    useEffect(() => { if (productId) dispatch(fetchRelatedProducts(productId)); }, [productId, dispatch]);

    const checkScroll = useCallback(() => { if (scrollRef.current) { const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current; setShowLeft(scrollLeft > 0); setShowRight(scrollLeft < scrollWidth - clientWidth - 1); } }, []);
    useEffect(() => { if (relatedProducts.length > 0) setTimeout(checkScroll, 100); }, [relatedProducts.length, checkScroll]);
    useEffect(() => { const c = scrollRef.current; if (c) { c.addEventListener('scroll', checkScroll); return () => c.removeEventListener('scroll', checkScroll); } }, [checkScroll]);
    const scroll = (dir) => { if (scrollRef.current) { scrollRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' }); setTimeout(checkScroll, 300); } };

    if (!isLoading && relatedProducts.length === 0) return null;
    return (
        <div className='related-products-section'>
            <h3 className='related-products-title'>Похожие товары</h3>
            {isLoading && relatedProducts.length === 0 ? (
                <div className='related-products-loading'>{Array(4).fill(null).map((_, i) => (<div key={i} className='related-product-card-wrapper'><ItemCardSkeleton /></div>))}</div>
            ) : (
                <div className='related-products-carousel-container'>
                    {showLeft && <button className='scroll-btn left' onClick={() => scroll('left')}><LeftOutlined /></button>}
                    <div className='related-products-carousel' ref={scrollRef}>
                        {relatedProducts.map(p => (<div className='related-product-card-wrapper' key={p.id}><ItemCard id={p.id} main_image={p.main_image} name={p.name} short_description={p.short_description} average_rating={p.average_rating} rating_count={p.rating_count} price={p.price} seller_company={p.seller_company} categories={p.categories} item={p} /></div>))}
                    </div>
                    {showRight && <button className='scroll-btn right' onClick={() => scroll('right')}><RightOutlined /></button>}
                </div>
            )}
        </div>
    );
};

const fallbackAttrLabels = { 'pa_package-type': 'Выберите упаковку', 'pa_warehouse': 'Выберите склад отгрузки', 'pa_sizeof': 'Выберите размер', 'pa_color': 'Выберите цвет', 'pa_vet': 'Выберите специалиста', 'pa_pcr': 'Выберите тип исследования' };
const getFallbackLabel = (attr) => fallbackAttrLabels[attr.slug] || `Выберите ${attr.name.toLowerCase()}`;

const ProductPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { productId } = useParams();
    const reviewsRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [selectedVariations, setSelectedVariations] = useState({});
    const [selectedPackaging, setSelectedPackaging] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);

    const { selectProductsById, selectIsItemLoading } = productsSelectors;
    const { selectIsItemInCart, selectItemQuantityInCart } = cartSelectors;
    const { selectReviewsByProductId, selectIsReviewsLoading, selectReviewsHasMore, selectReviewsTotal } = reviewsSelectors;

    const productFromStore = useSelector((state) => selectProductsById(state))?.[productId];
    const isLoading = useSelector((state) => selectIsItemLoading(state, productId));
    const isInCart = useSelector((state) => selectIsItemInCart(state, productFromStore?.id));
    const quantityInCart = useSelector((state) => selectItemQuantityInCart(state, productFromStore?.id));
    const cartItems = useSelector((state) => state.cart.items);
    const isFavorite = useSelector((state) => selectIsItemFavorite(state, productFromStore?.id));
    const reviews = useSelector((state) => selectReviewsByProductId(state, productId));
    const reviewsLoading = useSelector((state) => selectIsReviewsLoading(state, productId));
    const reviewsHasMore = useSelector((state) => selectReviewsHasMore(state, productId));
    const totalReviews = useSelector((state) => selectReviewsTotal(state, productId));
    const attributes = productFromStore?.attributes;

    const useNewLogic = hasPackagingOptions(productFromStore);
    const isService = productFromStore?.categories?.some(c => c.slug === 'services' || c.slug === 'servces');
    const isSinglePackaging = useNewLogic && productFromStore?.packaging_options?.length === 1;

    const showCitySelect = useMemo(
        () => useNewLogic && hasCityValues(productFromStore?.packaging_options),
        [productFromStore?.packaging_options, useNewLogic]
    );

    const { firstLabel, secondLabel } = useMemo(
        () => useNewLogic ? getSelectLabels(productFromStore) : { firstLabel: '', secondLabel: '' },
        [productFromStore?.attributes, useNewLogic]
    );

    const searchableFirst = useMemo(
        () => useNewLogic && isFirstSelectSearchable(productFromStore),
        [productFromStore?.attributes, useNewLogic]
    );

    const packagings = useMemo(
        () => useNewLogic ? getUniquePackagings(productFromStore?.packaging_options) : [],
        [productFromStore?.packaging_options, useNewLogic]
    );

    const availableCities = useMemo(() => {
        if (!useNewLogic || !showCitySelect || !selectedPackaging) return [];
        return getCitiesForPackaging(productFromStore?.packaging_options, selectedPackaging);
    }, [productFromStore?.packaging_options, selectedPackaging, useNewLogic, showCitySelect]);

    const matchedOption = useMemo(() => {
        if (!useNewLogic || !selectedPackaging) return null;
        if (showCitySelect) {
            return selectedCity ? findOption(productFromStore?.packaging_options, selectedPackaging, selectedCity) : null;
        }
        return findOption(productFromStore?.packaging_options, selectedPackaging, null);
    }, [productFromStore?.packaging_options, selectedPackaging, selectedCity, useNewLogic, showCitySelect]);

    // Fallback
    const variationAttrs = useMemo(() => !useNewLogic ? getVariationAttributes(attributes) : [], [attributes, useNewLogic]);
    const infoAttributes = useMemo(() => getNonVariationAttributes(attributes), [attributes]);

    // Init packaging_options
    useEffect(() => {
        if (!productFromStore || !useNewLogic || packagings.length === 0) return;
        const firstPkg = packagings[0];
        setSelectedPackaging(firstPkg);
        if (showCitySelect) {
            const cities = getCitiesForPackaging(productFromStore.packaging_options, firstPkg);
            setSelectedCity(cities.length > 0 ? cities[0] : null);
        }
    }, [productFromStore?.id, useNewLogic]);

    // Init fallback
    useEffect(() => {
        if (!productFromStore || useNewLogic || variationAttrs.length === 0) return;
        const defaults = {};
        variationAttrs.forEach(attr => {
            const da = productFromStore.default_attributes?.find(d => d.id === attr.id || d.name === attr.name);
            if (da && attr.options.length > 0) { const match = attr.options.find(opt => opt.toLowerCase().includes((da.option || '').replace(/-/g, ' ').toLowerCase())); defaults[attr.slug] = match || attr.options[0]; }
            else if (attr.options.length > 0) { defaults[attr.slug] = attr.options[0]; }
        });
        setSelectedVariations(defaults);
    }, [productFromStore?.id, useNewLogic]);

    useEffect(() => { if (productId) { dispatch(fetchProductById(productId)); dispatch(fetchReviewsList(productId, 1)); } }, [dispatch, productId]);

    const checkScrollPosition = () => { if (reviewsRef.current) { const { scrollLeft, scrollWidth, clientWidth } = reviewsRef.current; setShowLeftArrow(scrollLeft > 0); setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1); } };
    const handleReviewsScroll = () => { checkScrollPosition(); if (reviewsRef.current) { const { scrollLeft, scrollWidth, clientWidth } = reviewsRef.current; if ((scrollLeft + clientWidth) / scrollWidth > 0.8 && reviewsHasMore && !reviewsLoading) dispatch(fetchNextReviewsPage(productId)); } };
    useEffect(() => { if (reviews.length > 0) setTimeout(checkScrollPosition, 100); }, [reviews.length]);
    useEffect(() => { const c = reviewsRef.current; if (c) { c.addEventListener('scroll', handleReviewsScroll); return () => c.removeEventListener('scroll', handleReviewsScroll); } }, [reviewsHasMore, reviewsLoading, productId]);

    const handleVariationChange = useCallback((slug, value) => { setSelectedVariations(prev => ({ ...prev, [slug]: value })); }, []);
    const handlePackagingChange = (value) => {
        setSelectedPackaging(value);
        if (showCitySelect) {
            const cities = getCitiesForPackaging(productFromStore?.packaging_options, value);
            setSelectedCity(cities.length > 0 ? cities[0] : null);
        }
    };

    const currentVariationInCart = useMemo(() => {
        if (!productFromStore) return null;
        const pid = productFromStore.id;
        let vid;
        if (useNewLogic && matchedOption) {
            vid = matchedOption.variation_id;
        } else if (!useNewLogic) {
            vid = Object.values(selectedVariations).filter(Boolean).join('_') || null;
        }
        const cartItemId = vid ? `${pid}_${vid}` : `${pid}`;
        return cartItems.find(item => item.cartItemId === cartItemId) || null;
    }, [cartItems, productFromStore?.id, useNewLogic, matchedOption, selectedVariations]);

    if (isLoading) return <ProductSkeleton />;
    if (!productFromStore) return <div className='product-page-container'>Товар с ID: {productId} не найден.</div>;

    const product = productFromStore;
    const { id, main_image, name, average_rating: rate, description, short_description, seller_company: seller, price } = product;
    const image = product.images?.[0]?.src || main_image;
    const reviewsCount = reviews.length > 0 ? reviews.length : totalReviews;
    const reviewsCountText = reviewsCount === 0 ? 'Нет отзывов' : reviewsCount === 1 ? '1 отзыв' : reviewsCount < 5 ? `${reviewsCount} отзыва` : `${reviewsCount} отзывов`;
    const plainDescription = stripHtml(short_description) || stripHtml(description);

    const handleAddToCart = () => {
        if (useNewLogic && matchedOption) {
            dispatch(addToCart({ ...product, packaging: selectedPackaging || '', warehouse: selectedCity || '', variationId: matchedOption.variation_id, price: matchedOption.price, selectedVariations: { packaging: selectedPackaging, ...(selectedCity ? { city: selectedCity } : {}) } }, 1));
        } else {
            dispatch(addToCart({ ...product, packaging: selectedVariations['pa_package-type'] || '', warehouse: selectedVariations['pa_warehouse'] || '', selectedVariations: { ...selectedVariations }, variationId: Object.values(selectedVariations).filter(Boolean).join('_') || null }, 1));
        }
    };

    const handleChangeQuantity = (qty) => dispatch(changeCartItemQuantity(`${id}`, qty));
    const handleRemoveFromCart = () => dispatch(cartActions.removeItem(`${id}`));
    const scrollReviews = (dir) => { if (reviewsRef.current) { reviewsRef.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' }); setTimeout(checkScrollPosition, 300); } };

    const DescriptionContent = () => (<div className='product-full-description'>{description ? <div dangerouslySetInnerHTML={{ __html: description }} /> : short_description ? <div dangerouslySetInnerHTML={{ __html: short_description }} /> : <p>Описание отсутствует</p>}</div>);

    const NutritionContent = () => {
        if (infoAttributes.length === 0) return <p>Нет данных</p>;
        const mid = Math.ceil(infoAttributes.length / 2);
        return (
            <div className='product-full-description'>
                <div className='product-attributes-two-col'>
                    <table className='product-attributes-table'><tbody>{infoAttributes.slice(0, mid).map((attr, idx) => (<tr key={idx}><td className='attr-name'>{attr.name}</td><td className='attr-value'>{attr.options.join(', ')}</td></tr>))}</tbody></table>
                    {infoAttributes.slice(mid).length > 0 && <table className='product-attributes-table'><tbody>{infoAttributes.slice(mid).map((attr, idx) => (<tr key={idx}><td className='attr-name'>{attr.name}</td><td className='attr-value'>{attr.options.join(', ')}</td></tr>))}</tbody></table>}
                </div>
            </div>
        );
    };

    const isNutritionProduct = infoAttributes.some(attr => /протеин|клетчатка|жир|зола|влажность|кальций|фосфор|сахар|крахмал/i.test(attr.name));

    const customComposition = product.custom_composition;
    const customIndicators = product.custom_indicators;
    const customRecommendations = product.custom_recommendations;
    const customInstructions = product.custom_instructions;

    const tabItems = [
        { key: '1', label: 'Описание', children: <DescriptionContent /> },
        ...(customComposition ? [{ key: 'composition', label: 'Состав', children: <div className='product-full-description'><div dangerouslySetInnerHTML={{ __html: customComposition }} /></div> }] : []),
        ...(customIndicators ? [{ key: 'indicators', label: 'Показатели', children: <div className='product-full-description'><div dangerouslySetInnerHTML={{ __html: customIndicators }} /></div> }] : []),
        ...(customRecommendations ? [{ key: 'recommendations', label: 'Рекомендации', children: <div className='product-full-description'><div dangerouslySetInnerHTML={{ __html: customRecommendations }} /></div> }] : []),
        ...(customInstructions ? [{ key: 'instructions', label: 'Инструкция', children: <div className='product-full-description'><div dangerouslySetInnerHTML={{ __html: customInstructions }} /></div> }] : []),
        ...(!customComposition && infoAttributes.length > 0 ? [{ key: '2', label: isNutritionProduct ? 'Состав' : 'Параметры', children: <NutritionContent /> }] : []),
    ];

    const hasVars = useNewLogic || variationAttrs.length > 0;

    return (
        <div className='product-page-container'>
            <div className='product-top-section'>
                <div className='product-image-block'>
                    {image ? <img className='product-detail-img' src={image} alt={name} /> : <div className='product-detail-img-placeholder'>Нет изображения</div>}
                    <button
                        className={`product-detail-favorite ${isFavorite ? 'active' : ''}`}
                        onClick={() => dispatch(toggleFavorite(productFromStore))}
                        aria-label={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'}
                    >
                        {isFavorite ? <HeartFilled /> : <HeartOutlined />}
                    </button>
                </div>

                <div className='product-info-block'>
                    <h1 className='product-detail-title'>{name || 'Без названия'}</h1>
                    {/*<div className='product-detail-rating'>*/}
                    {/*    <ConfigProvider theme={{ components: { Rate: { starBg: 'rgba(255,255,255,0.3)' } } }}>*/}
                    {/*        <Rate defaultValue={Number(rate) || 0} disabled style={{ color: '#DC0202', fontSize: '14px' }} />*/}
                    {/*    </ConfigProvider>*/}
                    {/*    <span className='product-detail-reviews-count'>{reviewsCountText}</span>*/}
                    {/*</div>*/}
                    {plainDescription && <p className='product-detail-short-desc'>{plainDescription.length > 260 ? plainDescription.slice(0, 260) + '...' : plainDescription}</p>}
                    {/*<div className='product-detail-supplier-ctn'>*/}
                    {/*    <div className='product-detail-supplier-title'>Поставщик</div>*/}
                    {/*    <div className='product-detail-supplier-info'>*/}
                    {/*        {seller?.logo && <img className='product-detail-supplier-img' src={seller.logo} alt={seller.name} />}*/}
                    {/*        <span className='product-detail-supplier-text'>{seller?.name || 'Неизвестно'}</span>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </div>

                <div className='product-price-card'>
                    <PriceDisplay product={product} matchedOption={matchedOption} />

                    {useNewLogic ? (
                        <>
                            {!(isService && isSinglePackaging) && (
                                <div className='price-card-field'>
                                    <label>{firstLabel}</label>
                                    <Select
                                        value={selectedPackaging || undefined}
                                        onChange={handlePackagingChange}
                                        style={{ width: '100%' }}
                                        options={toSelectOptions(packagings)}
                                        placeholder={firstLabel}
                                        notFoundContent="Нет данных"
                                        {...(searchableFirst ? {
                                            showSearch: true,
                                            filterOption: (input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
                                        } : {})}
                                    />
                                </div>
                            )}

                            {showCitySelect && !(isService && isSinglePackaging) && (
                                <div className='price-card-field'>
                                    <label>{secondLabel}</label>
                                    <Select
                                        value={selectedCity || undefined}
                                        onChange={(val) => setSelectedCity(val)}
                                        style={{ width: '100%' }}
                                        options={toSelectOptions(availableCities)}
                                        placeholder={secondLabel}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                        disabled={!selectedPackaging}
                                        notFoundContent="Нет данных"
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        variationAttrs.map(attr => (
                            <div className='price-card-field' key={attr.slug}>
                                <label>{getFallbackLabel(attr)}</label>
                                <Select value={selectedVariations[attr.slug] || undefined} onChange={(val) => handleVariationChange(attr.slug, val)} style={{ width: '100%' }} options={toSelectOptions(attr.options)} placeholder={getFallbackLabel(attr)} notFoundContent="Нет данных" {...(attr.slug === 'pa_warehouse' ? { showSearch: true, filterOption: (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) } : {})} />
                            </div>
                        ))
                    )}

                    {hasVars ? (
                        <>
                            {currentVariationInCart ? (
                                <Button block className='go-to-cart-link-btn' onClick={() => navigate('/cart')} icon={<ShoppingCartOutlined />}>В корзине ({currentVariationInCart.quantity} шт.)</Button>
                            ) : (
                                <Button type="primary" block className='add-to-cart-btn' icon={<ShoppingCartOutlined />} onClick={handleAddToCart}>Добавить в корзину</Button>
                            )}
                        </>
                    ) : isInCart ? (
                        <>
                            <div className='product-cart-controls'>
                                <div className='product-quantity-stepper'>
                                    <button className="product-qty-btn" onClick={() => handleChangeQuantity(quantityInCart - 1)} disabled={quantityInCart <= 1}><MinusOutlined /></button>
                                    <div className="product-qty-value">{quantityInCart}</div>
                                    <button className="product-qty-btn" onClick={() => handleChangeQuantity(quantityInCart + 1)}><PlusOutlined /></button>
                                </div>
                                <button className="product-delete-btn" onClick={handleRemoveFromCart}><DeleteFilled /></button>
                            </div>
                            <Button block className='go-to-cart-link-btn' onClick={() => navigate('/cart')}>Перейти в корзину</Button>
                        </>
                    ) : (
                        <Button type="primary" block className='add-to-cart-btn' icon={<ShoppingCartOutlined />} onClick={handleAddToCart}>Добавить в корзину</Button>
                    )}
                </div>
            </div>

            <div className='product-divider' />

            <div className='product-tabs-wrapper'>
                <ConfigProvider theme={{ components: { Tabs: { itemColor: '#818181', itemSelectedColor: '#ffffff', itemHoverColor: '#cccccc', inkBarColor: '#ffffff', titleFontSize: 16 } } }}>
                    <Tabs defaultActiveKey="1" items={tabItems} className="custom-dark-tabs" />
                </ConfigProvider>
            </div>

            {/* TODO: временно скрыто */}
            {false && reviews.length > 0 && (
                <div className='reviews-section'>
                    <h3 className='reviews-title'>Отзывы <span className='reviews-count'>{reviewsCountText}</span></h3>
                    <div className='reviews-rating-stars'>
                        <ConfigProvider theme={{ components: { Rate: { starBg: 'rgba(255,255,255,0.3)' } } }}>
                            <Rate defaultValue={Number(rate) || 0} disabled style={{ color: '#DC0202', fontSize: '14px' }} />
                        </ConfigProvider>
                    </div>
                    <div className='reviews-carousel-container'>
                        {showLeftArrow && <button className='scroll-btn left' onClick={() => scrollReviews('left')}><LeftOutlined /></button>}
                        <div className='reviews-carousel' ref={reviewsRef}>
                            {reviews.map((review) => (
                                <div key={review.id} className='review-card'>
                                    <div className='review-header'>
                                        <div className='review-avatar' />
                                        <div className='review-user-info'>
                                            <div className='review-username'>{review.user_name || review.user || 'Аноним'}<span className='review-date'>{review.created_at || review.date}</span></div>
                                            <Rate defaultValue={review.rating || review.rate || 0} disabled style={{ color: '#DC0202', fontSize: '12px' }} />
                                        </div>
                                    </div>
                                    <div className='review-body'>
                                        {review.pros && <div className='review-row'><strong>Плюсы:</strong><br/>{review.pros}</div>}
                                        {review.cons && <div className='review-row'><strong>Минусы:</strong><br/>{review.cons}</div>}
                                        {(review.comment || review.text) && <div className='review-row'><strong>Отзыв:</strong><br/>{review.comment || review.text}</div>}
                                    </div>
                                </div>
                            ))}
                            {reviewsLoading && reviews.length > 0 && reviewsHasMore && (<div className='review-card' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Skeleton active avatar paragraph={{ rows: 3 }} /></div>)}
                        </div>
                        {showRightArrow && <button className='scroll-btn right' onClick={() => scrollReviews('right')}><RightOutlined /></button>}
                    </div>
                </div>
            )}

            <RelatedProducts productId={productId} />
        </div>
    );
};

export default ProductPage;